/**
 * This module contains UI
 * widgets to represent instructions
 *
 *
 */


(function() {
  const { controlCenter, machineBuilder } = require('@ventionco/mm-machine-logic-robot');

  const $ = require('jQuery');
  const instruction_widgets = require('./instruction_widgets');
  const motor_instruction = require('./gui_instructions/motor_instruction');
  const waitFor_instruction = require('./gui_instructions/waitFor_instruction');
  const output_instruction = require('./gui_instructions/output_instruction');
  const trigger_instruction = require('./gui_instructions/trigger_instruction');
  const setVariable_instruction = require('./gui_instructions/setVariable_instruction');
  const log_instruction = require('./gui_instructions/log_instruction');
  const environment = require('../environment');
  const { fsm }  = require('./gui_instructions/fsm');
  const tooltips = require("./machinelogic_tooltips");

  function create(library, application, sequence, instruction) {
    const lInstructionCategory = instruction.getCategory();
    const lParams = { library, application, sequence, instruction };

    const getInstructionIndex = () => sequence.getInstructionIndex(instruction) !== -1 ? sequence.getInstructionIndex(instruction) : sequence.mInstructions.length;

    if (/^(condition|loop)/i.test(lInstructionCategory)) {
      return conditionalInstruction(lParams);
    } else if (/motor/i.test(lInstructionCategory)) {
      return motor_instruction.create(lParams);
    } else if (/waitFor/i.test(lInstructionCategory)) {
      return waitFor_instruction.create(lParams);
    } else if (/output/i.test(lInstructionCategory)) {
      return output_instruction.create(lParams);
    } else if (/fsm/i.test(lInstructionCategory)) {
      return fsm(lParams);
      // trigger -> UI calls execution
    } else if (/^trigger$/i.test(lInstructionCategory)) {
      return trigger_instruction.create(lParams);
    } else if (/setVariable/i.test(lInstructionCategory)) {
      return setVariable_instruction.create(lParams);
    } else if (/robotLoop/i.test(lInstructionCategory)) {
      const { view: { RobotLoopInstruction: controlCenterRobotLoopInstruction } } = controlCenter;
      const { view: { RobotLoopInstruction: machineBuilderRobotLoopInstruction } } = machineBuilder;

      const instructionIndex = getInstructionIndex();
      if (environment.isOnCad()) {
        const ctx3D = library.m3DContext;
        const robotAppsService = ctx3D.robotAppsService;
        return machineBuilderRobotLoopInstruction.create({ ...lParams, robotAppsService, tooltips }, $('<div class="ml_instruction h_layout"/>'));
      } else if (environment.isOnPendant()) {
        const robotAppsService = library.robotAppsService.selectors.getRobotAppsService();
        return controlCenterRobotLoopInstruction.create({ ...lParams, instructionIndex, robotAppsService, tooltips }, $('<div class="ml_instruction h_layout"/>'));
      }
    } else if (/^robot$/i.test(lInstructionCategory)) {

      const { view: { RobotInstruction: controlCenterRobotInstruction }, model: { createEmptyFreedriveStatus }}  = controlCenter;
      const { view: { RobotInstruction: machineBuilderRobotInstruction } } = machineBuilder;

      const getClipById = id => sequence?.mApp?.robotParameters?.clips.find(c => c.id === id) || sequence?.robotParameters?.clips.find(c => c.id === id);
      const isLocalSequenceClip = clip => !!sequence.robotParameters.clips.find(c => c.id === clip?.id);
      const getRobotSimulationState = () => instruction.robotSimulationState;
      const getSelectedClip = () => getClipById(instruction.getParameter('clipId'));
      const getExecutionReferenceFrame = () => instruction.getParameter('executionReferenceFrame');

      if (environment.isOnCad()) {
        const ctx3D = library.m3DContext;
        const robotUtils = ctx3D.robotUtils;
        const robotAppsService = ctx3D.robotAppsService;
        const manipRobotState = ctx3D.getManipulatorOfType("manip-robot")?.mState;

        const getRobotConfig = robotId => library.getConfigManager().listRobots().find(r => r.mRobotId === robotId);
        const getRobotActuatorVIDFromRobot = robotConfig => robotConfig?.mRobotActuators && parseInt(Object.keys(robotConfig.mRobotActuators)[0]);
        const getRobotActuatorVID = () => getRobotActuatorVIDFromRobot(robotConfig);
        const getJointLimits = () => robotUtils.getRobotJointLimits(getRobotActuatorVID());
        const getCalibrationReferenceFrame = () => robotUtils.getInstructionCalibrationReferenceFrame(instruction.getParameter('calibrationReferenceFrame')?.vid, getRobotActuatorVID());

        const robotId = instruction.getRobotId();
        const robotConfig = getRobotConfig(robotId);
        const robotType = robotConfig?.mType;

        return machineBuilderRobotInstruction.create({
          ...lParams,
          robotUtils,
          robotAppsService,
          manipRobotState,
          tooltips,
          getJointLimits,
          getRobotActuatorVID,
          getSelectedClip,
          getExecutionReferenceFrame,
          getCalibrationReferenceFrame,
          getRobotSimulationState,
          getInstructionIndex,
          isLocalSequenceClip,
          childrenApis: {
            common: {
              validatePoseWaypoint: pose => manipRobotState ? manipRobotState.validatePoseWaypoint({ vid: getRobotActuatorVID(), pose }) : false
            },
            selector: {
              robotType,
              getArrayMatrixFromWorldByReferenceFrame: robotUtils.getArrayMatrixFromWorldByReferenceFrame,
            },
            /**
             * An object containing various functions related to robot state manipulation.
             * @typedef {Object} EditableRobotState - used in ControlCenter and MachineBuilder for all editable robot instructions
             * @property {() => array | undefined} getArrayRobotDigitalIOValuesFromRobotVID
             * @property {() => array | undefined} getDefaultTeachingReferenceFrame
             * @property {() => object | undefined} get___matrix__endEffector_referenceFrame__translation
             * @property {() => object | undefined} get___matrix__endEffector_referenceFrame__rotation
             * @property {() => array | undefined} getMatrixEndEffectorFromWorld
             * @property {() => array | undefined} getJointAnglesDegrees
             */
            editable: {
              getArrayRobotDigitalIOValuesFromRobotVID: () => manipRobotState?.getArrayRobotDigitalIOValuesFromRobotVID(getRobotActuatorVID()),
              getDefaultTeachingReferenceFrame: () => robotUtils.createTeachingReferenceFrameAtRobotBase(getRobotActuatorVID()),
              get___matrix__endEffector_referenceFrame__translation: manipRobotState?.get___matrix__endEffector_referenceFrame__translation,
              get___matrix__endEffector_referenceFrame__rotation: manipRobotState?.get___matrix__endEffector_referenceFrame__rotation,
              getJointAngles: manipRobotState?.getJointAngles,
              getMatrixEndEffectorFromWorld: manipRobotState?.getMatrixEndEffectorFromWorld,
              getJointAnglesDegrees: manipRobotState?.getJointAnglesDegrees,
            },
          },
        }, $('<div class="ml_instruction h_layout"/>'));
      } else {
        const robotAppsService = library.robotAppsService.selectors.getRobotAppsService();

        const manipRobotState = {
          getArrayRobotDigitalIOValuesFromRobotVID: () => [],
        };

        const getJointLimits = () => {};
        const getCalibrationReferenceFrame = () => instruction.getParameter('calibrationReferenceFrame');
        const getRobotActuatorVID = () => library.robotAppsService.selectors.getRobotConfig()?.parameters.robotId;

        const applicationReferenceFrames = library.robotAppsService.selectors.getReferenceFramesByApplicationId(application.mId);

        return controlCenterRobotInstruction.create({
          ...lParams,
          robotAppsService,
          manipRobotState,
          selectors: library.robotAppsService.selectors,
          tooltips,
          getJointLimits,
          getRobotActuatorVID,
          getSelectedClip,
          getExecutionReferenceFrame,
          getCalibrationReferenceFrame,
          getCalibrationReferenceFrames: () => [{ name: "Robot_Base" }, ...(applicationReferenceFrames ? Object.values(applicationReferenceFrames) : [])],
          getRobotSimulationState,
          getInstructionIndex,
          isLocalSequenceClip,
          childrenApis: {
            editable: {
              getArrayRobotDigitalIOValuesFromRobotVID: () => manipRobotState.getArrayRobotDigitalIOValuesFromRobotVID(getRobotActuatorVID()),
              getDefaultTeachingReferenceFrame: () => ({}),
              get___matrix__endEffector_referenceFrame__translation: manipRobotState.get___matrix__endEffector_referenceFrame__translation,
              get___matrix__endEffector_referenceFrame__rotation: manipRobotState.get___matrix__endEffector_referenceFrame__rotation,
              jogger: {
                getPosition: () => {
                  const position = library.robotAppsService.selectors.getRobotCartesianPositionInMM();

                  return {
                    mP: {
                      x: position?.x || 0,
                      y: position?.y || 0,
                      z: position?.z || 0,
                    }
                  };
                },
                getRotation: () => {
                  const rotation = library.robotAppsService.selectors.getRobotCartesianRotationInDeg();

                  return {
                    mP: {
                      x: rotation?.x || 0,
                      y: rotation?.y || 0,
                      z: rotation?.z || 0,
                    }
                  };
                },
                getJointAngles: () => library.robotAppsService.selectors.getRobotJointStatesInDeg() || [0, 0, 0, 0, 0, 0],
                getFreedriveStatus: () => library.robotAppsService.selectors.getFreedriveStatus() || createEmptyFreedriveStatus(),
              },
              getMatrixEndEffectorFromWorld: manipRobotState.getMatrixEndEffectorFromWorld,
              getJointAnglesDegrees: manipRobotState.getJointAnglesDegrees,
            },
          },
        }, $('<div class="ml_instruction h_layout"/>'));
      }


    } else if (/log/i.test(lInstructionCategory)) {
      return log_instruction.create(lParams);
    }
  }

  function conditionalInstruction(pParams) {
    const lDiv = $('<div class="ml_instruction h_layout"/>');
    let lCondition;

    if (pParams.instruction.getCategory().match(/condition/i)) {
      lCondition = instruction_widgets.condition(pParams);
    } else if (pParams.instruction.getCategory().match(/loop/i)) {
      lCondition = instruction_widgets.loop(pParams);
    }

    lDiv.append(lCondition);
    return lDiv;
  }

  /* Module export glue */
  /* global define */
  const gExports = {
    create: create,
  };
  const gModule = typeof module;
  if (gModule !== 'undefined' && module.exports != undefined) {
    module.exports = gExports;
  } else if (define != undefined && typeof define === 'function' && define.amd) {
    define([], function () { return gExports; });
  }
}());
